import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, StatusPill } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Clock, FileText, Plus } from "lucide-react";
import { cachedGet } from "../lib/offline";

export default function WorkerDay() {
  const { t, tn } = useI18n();
  const nav = useNavigate();
  const [days, setDays] = useState([]);
  const [reports, setReports] = useState([]);
  const [wt, setWt] = useState([]);
  const [units, setUnits] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/worktime").then((r) => setDays(r.data.filter((d) => d.date === today))).catch(() => {});
    api.get("/reports").then(async (r) => {
      const mine = r.data.filter((x) => x.date === today);
      const full = await Promise.all(mine.map((x) => api.get(`/reports/${x.id}`).then((f) => f.data)));
      setReports(full);
    }).catch(() => {});
    cachedGet("/config/work-types").then(setWt).catch(() => {});
    cachedGet("/config/units").then(setUnits).catch(() => {});
  }, [today]);

  const hours = days.reduce((s, d) => s + (d.hours || 0), 0);
  const active = days.find((d) => !d.end_time);
  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="worker-day">
      <PageHeader title={t("my_day")} subtitle={new Date().toLocaleDateString()} />
      <div className="grid grid-cols-2 gap-3">
        <div className="tactical-card p-4 kpi-accent">
          <div className="text-xs uppercase text-[var(--text-3)] font-semibold flex items-center gap-1"><Clock size={13} /> {t("hours_today")}</div>
          <div className="font-mono-num text-3xl font-bold mt-1" data-testid="day-hours">{hours.toFixed(1)}h</div>
          <div className="text-xs text-[var(--text-3)] mt-1">{active ? t("working") : days.length ? t("end_work") : t("not_started")}</div>
        </div>
        <div className="tactical-card p-4">
          <div className="text-xs uppercase text-[var(--text-3)] font-semibold flex items-center gap-1"><FileText size={13} /> {t("todays_report")}</div>
          <div className="mt-2">{reports[0] ? <StatusPill status={reports[0].status} /> : <span className="text-sm text-[var(--text-3)]">{t("no_report_yet")}</span>}</div>
        </div>
      </div>

      {days.length > 0 && (
        <div className="tactical-card divide-y">
          {days.map((d) => (
            <div key={d.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="font-mono-num">{fmt(d.start_time)} → {fmt(d.end_time)}</span>
              <span className="font-mono-num font-semibold">{d.end_time ? `${d.hours}h` : <span className="text-[var(--green)]">●</span>}</span>
            </div>
          ))}
        </div>
      )}

      <div className="tactical-card p-4">
        <div className="text-xs uppercase text-[var(--text-3)] font-semibold mb-2">{t("today_done")}</div>
        {reports.flatMap((r) => r.items).length === 0
          ? <div className="text-sm text-[var(--text-3)] text-center py-3">{t("no_data")}</div>
          : reports.flatMap((r) => r.items).map((it) => (
            <div key={it.id} className="flex items-center gap-2 text-sm py-1.5 border-b last:border-0">
              <span className="flex-1 truncate">{tn(wt.find((w) => w.id === it.work_type_id)?.name) || "…"}</span>
              <span className="font-mono-num font-semibold">{it.quantity} {tn(units.find((u) => u.id === it.unit_id)?.name)}</span>
            </div>
          ))}
      </div>

      <Button onClick={() => nav("/worker")} className="w-full h-14 bg-[var(--amber)] text-white font-bold gap-2 rounded-2xl" data-testid="day-add-work">
        <Plus size={20} /> {t("add_work")}
      </Button>
    </div>
  );
}
